import React, { ReactNode, useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useRequest } from 'ahooks'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonType,
  ActionButtonSize,
} from '$extensionSrc/components/ActionButton'
import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import Toast, { Props as ToastProps } from '$extensionSrc/components/Toast'
import { confirm2faPath, send2faSmsPath } from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { unauthClient } from '$serverSrc/apolloClientUnauth'
import {
  login2faMutation,
  requestLogin2faCodeMutation,
} from '$serverSrc/data/queryUnauth'

import FormSubmitButton from './FormSubmitButton'
import FormTextInput from './FormTextInput'
import { authFetch } from '../utils/fetch'

import './Confirm2faForm.scss'

function isValidCode(code: string) {
  return code.length === 6
}

export default function Confirm2faForm({
  onSkip,
  onSuccess,
  phoneNumber,
  showSkip,
  twofaMethod,
  userFlow,
}: {
  onSkip?: () => void
  onSuccess: () => void
  phoneNumber?: string
  showSkip: boolean
  twofaMethod: 'app' | 'sms'
  userFlow: 'signup' | 'login'
}) {
  const [statusToast, setStatusToast] = useState<ToastProps | null>(null)

  const defaultValues = { code: '' }
  const requiredValidations = {
    code: (x) => isValidCode(x),
  }

  const { getFieldChangeHandler, isValid, values } = useForm({
    defaultValues,
    requiredValidations,
  })

  const { run: sendSmsRequest } = useRequest(
    () => {
      setStatusToast(null)
      const formData = new FormData()
      if (phoneNumber) {
        formData.append('phone', phoneNumber)
      }

      return authFetch(
        new Request(send2faSmsPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      throttleWait: 1000 * 5, // 5 seconds
      throttleTrailing: false, // User must resubmit after throttle period
      onSuccess: (response: Response) => {
        if (response.status === 200) {
          setStatusToast({
            text: 'Your new code has been sent.',
            color: 'yellow',
            dismissAfterMs: 3000,
          })
        } else {
          setStatusToast({
            text: 'Failed to resend code.',
            color: 'red',
            dismissAfterMs: 6000,
            showCloseIcon: true,
          })
        }
      },
    },
  )

  const { run: submitCodeRequest } = useRequest(
    (token) => {
      const formData = new FormData()
      formData.append('token', token)
      formData.append('2faMethod', twofaMethod)

      return authFetch(
        new Request(confirm2faPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      throttleWait: 500,
      throttleTrailing: false, // User must resubmit after throttle period
      onSuccess: (response: Response) => {
        if (response.status === 200) {
          onSuccess()
        } else {
          setStatusToast({
            text: 'Invalid code',
            color: 'red',
            noAutoDismiss: true,
            showCloseIcon: true,
          })
        }
      },
    },
  )

  const [submitLogin2faRequest, { data: loginResponseData }] = useMutation(
    login2faMutation,
    { client: unauthClient },
  )
  const login2faStatusCode = loginResponseData?.login2fa?.statusCode

  const [showResendButton, setShowResendButton] = useState(false)
  const [timeRemainingUntilResend, setTimeRemainingUntilResend] = useState(30)

  const resetResendTimer = () => {
    setShowResendButton(false)
    setTimeRemainingUntilResend(30)

    const interval = setInterval(() => {
      setTimeRemainingUntilResend((prev) => (prev <= 0 ? 0 : prev - 1))
    }, 1000)

    setTimeout(() => {
      clearInterval(interval)
      setShowResendButton(true)
    }, 30000)
  }

  useEffect(() => {
    resetResendTimer()
  }, [])

  const [submitResendLogin2faCodeRequest, { data: resendCodeResponseData }] =
    useMutation(requestLogin2faCodeMutation, { client: unauthClient })

  const resendLogin2faCodeStatusCode =
    resendCodeResponseData?.requestLogin2faCode?.statusCode

  if (login2faStatusCode === 200) {
    onSuccess()
  } else if (login2faStatusCode && !statusToast) {
    setStatusToast({
      text: 'Invalid Code',
      color: 'red',
      noAutoDismiss: true,
      showCloseIcon: true,
    })
  }

  if (resendLogin2faCodeStatusCode === 200 && !statusToast) {
    setStatusToast({
      text: 'Your new code has been sent.',
      color: 'yellow',
      dismissAfterMs: 3000,
    })
  } else if (resendLogin2faCodeStatusCode && !statusToast) {
    setStatusToast({
      text: 'Failed to resend code.',
      color: 'red',
      dismissAfterMs: 6000,
      showCloseIcon: true,
    })
  }

  const resendSms2faCode = () => {
    resetResendTimer()
    setStatusToast(null)
    switch (userFlow) {
      case 'signup':
        sendSmsRequest()
        break
      case 'login':
        submitResendLogin2faCodeRequest()
        break
      default:
    }
  }

  const submitForm = (event) => {
    setStatusToast(null)
    event.preventDefault()
    switch (userFlow) {
      case 'login':
        submitLogin2faRequest({
          variables: {
            token: values.code,
          },
        })
        break
      case 'signup':
        submitCodeRequest(values.code)
        break
      default:
    }
  }

  let title
  let subtitle
  let resendBlock: ReactNode | null = null
  switch (`${twofaMethod}|${userFlow}`) {
    case 'app|signup':
      title = 'Enter the code from your app'
      subtitle = `Check your authenticator app for your ${BuildConfig.TENANT_NAME} code.`
      break
    case 'app|login':
      title = "Verify that it's really you"
      subtitle = 'Enter the code in your authenticator app.'
      break
    case 'sms|signup':
      title = 'Enter the code you received'
      subtitle = `It was sent to your number ending in ${phoneNumber?.slice(
        -4,
      )}.`
      // TODO: Add "cool down" period for resending code, and add different message
      resendBlock = (
        <>
          Didn't get a code? You can{' '}
          {showResendButton ? (
            <InteractiveElement
              className="link2 cursor-pointer darkest-gray"
              onClick={resendSms2faCode}
            >
              get a new code
            </InteractiveElement>
          ) : (
            <>request a new code in {timeRemainingUntilResend}s</>
          )}
          .
        </>
      )
      break
    case 'sms|login':
      title = "Verify that it's really you"
      subtitle = `Enter the code sent to your number ending in ${phoneNumber?.slice(
        -4,
      )}.`
      resendBlock = (
        <>
          Didn't get a code? You can{' '}
          {showResendButton ? (
            <InteractiveElement
              className="link2 cursor-pointer darkest-gray"
              onClick={resendSms2faCode}
            >
              get a new code
            </InteractiveElement>
          ) : (
            <>request a new code in {timeRemainingUntilResend}s</>
          )}
          .
        </>
      )
      break
    default:
    // pass
  }

  return (
    <form className="Confirm2faForm" onSubmit={submitForm}>
      <div className="spacer" />
      <div className="enroll-confirm-content">
        <div>
          <div className="heading1 bold center">{title}</div>
          <div className="body1 darkest-gray center">{subtitle}</div>
        </div>
        <div className="form-content">
          <FormTextInput
            handleChange={getFieldChangeHandler('code')}
            isSensitive={false}
            name="code"
            placeholderText="Your 6-digit code"
            value={values.code}
            requirements={[
              {
                text: 'Enter a valid code',
                isFulfilled: isValidCode(values.code),
              },
            ]}
          />
        </div>
        <div className="resend-code-message body2 darkest-gray center">
          {resendBlock}
        </div>
      </div>
      <div className="buttons">
        <FormSubmitButton isValid={isValid}>continue</FormSubmitButton>
        {showSkip && (
          <ActionButton
            onClick={onSkip}
            type={ActionButtonType.TERTIARY}
            size={ActionButtonSize.MEDIUM}
          >
            Skip for now
          </ActionButton>
        )}
      </div>
      {statusToast && <Toast {...statusToast} />}
    </form>
  )
}
